<template>
  <TabPanel class="tw-w-full">
    <div class="tw-space-y-5">
      <div class="tw-grid tw-grid-cols-12 tw-gap-5">
        <RouterWanSection v-if="routerWanSupported" class="tw-col-span-full md:tw-col-span-6" />

        <!-- Routers without failover (MikroTik, older nate-runner): switch the WAN with a form. -->
        <Box v-else rounded="xlarge" class="tw-relative tw-col-span-full tw-space-y-5 tw-bg-white/5 md:tw-col-span-6">
          <div
            v-if="submitting"
            class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-[inherit] tw-backdrop-blur"
          >
            <Spinner size="medium" />

            <Text v-if="remainingSec !== null">{{ t('networking.remaining_seconds', { count: remainingSec }) }}</Text>
          </div>

          <div class="tw-space-y-1">
            <Heading level="h4">{{ t('networking.configure_wan_title') }}</Heading>
            <Text>{{ t('networking.configure_wan_subtitle') }}</Text>
          </div>

          <div class="tw-grid tw-grid-cols-12 tw-gap-5">
            <FormField
              class="tw-col-span-full"
              type="select"
              size="small"
              v-model="form.command"
              :label="t('networking.command')"
              :disabled="submitting"
            >
              <option value="wan_wifi">{{ t('networking.command_wan_wifi') }}</option>
              <option value="wan_ethernet">{{ t('networking.command_wan_ethernet') }}</option>
            </FormField>

            <template v-if="form.command === 'wan_wifi'">
              <FormField
                class="tw-col-span-full md:tw-col-span-6"
                size="small"
                v-model="form.wifiSsid"
                :label="t('networking.wifi_ssid')"
                :disabled="submitting"
                :field-attrs="{ class: 'tw-font-mono' }"
              />

              <FormField
                class="tw-col-span-full md:tw-col-span-6"
                size="small"
                v-model="form.wifiPassword"
                :label="t('networking.wifi_password')"
                :disabled="submitting"
                :field-attrs="{ class: 'tw-font-mono' }"
              />
            </template>
          </div>

          <div
            v-if="form.command === 'wan_wifi' && (ssidHasUnsafeChars || passwordHasUnsafeChars)"
            class="tw-space-y-1"
          >
            <Text v-if="ssidHasUnsafeChars" class="tw-font-black tw-text-orange">
              {{ t('networking.unsafe_chars_ssid') }}
            </Text>

            <Text v-if="passwordHasUnsafeChars" class="tw-font-black tw-text-orange">
              {{ t('networking.unsafe_chars_password') }}
            </Text>
          </div>

          <div v-if="form.command === 'wan_wifi'" class="tw-grid tw-grid-cols-12 tw-gap-5">
            <FormField
              class="tw-col-span-full md:tw-col-span-6"
              type="select"
              size="small"
              v-model="form.band"
              :label="t('networking.band')"
              :disabled="submitting"
            >
              <option value="2.4GHz">{{ t('networking.band_2_4') }}</option>
              <option value="5GHz">{{ t('networking.band_5') }}</option>
            </FormField>
          </div>

          <div v-if="form.command === 'wan_wifi'" class="tw-space-y-2">
            <Checkbox
              type="switch"
              v-model="form.revertWhenFails"
              :right-label="t('networking.revert_when_fails')"
              class="tw-px-2 tw-py-4"
              :disabled="submitting"
            />

            <Text v-if="form.revertWhenFails">{{ t('networking.revert_note') }}</Text>
          </div>

          <div v-if="form.command === 'wan_wifi' && form.revertWhenFails" class="tw-grid tw-grid-cols-12 tw-gap-5">
            <FormField
              class="tw-col-span-full md:tw-col-span-6"
              size="small"
              type="number"
              v-model="form.timeoutSec"
              :label="t('networking.timeout')"
              :disabled="submitting"
            />
          </div>

          <div class="tw-flex tw-flex-wrap tw-gap-3">
            <Button color="semiTransparentWhite" outlined @click="submit" :disabled="submitting || !canSubmit">
              {{ t('networking.submit') }}
            </Button>
          </div>
        </Box>

        <Box
          v-if="portForwardingSupported"
          rounded="xlarge"
          class="tw-col-span-full tw-space-y-5 tw-bg-white/5 md:tw-col-span-6"
        >
          <div class="tw-space-y-1">
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <Heading level="h4">{{ t('networking.port_forwarding_title') }}</Heading>

              <Button outlined size="small" class="tw-shrink-0" @click="portForwardingHelpOpen = true">
                <span class="tw-flex tw-items-center tw-gap-1.5">
                  <Icon :name="InformationOutlineSvg" class="tw-size-5 tw-fill-current" />
                  {{ t('networking.port_forwarding_help_button') }}
                </span>
              </Button>
            </div>
            <Text>{{ t('networking.port_forwarding_subtitle') }}</Text>
          </div>

          <Text class="tw-border-l-4 tw-border-orange tw-pl-3 tw-font-black tw-text-orange">
            {{ t('networking.port_forwarding_warning') }}
          </Text>

          <div class="tw-flex tw-items-center tw-gap-4">
            <Checkbox
              type="switch"
              :model-value="portForwardingEnabled"
              @update:model-value="setPortForwarding"
              :right-label="t('networking.port_forwarding_switch')"
              class="tw-px-2 tw-py-4"
              :disabled="portForwardingSubmitting || portForwardingUnknown"
            />

            <Spinner v-if="portForwardingSubmitting" size="small" />
          </div>

          <Text v-if="portForwardingUnknown" class="tw-font-black tw-text-orange">
            {{ t('networking.port_forwarding_unknown') }}
          </Text>

          <dl class="tw-grid tw-grid-cols-[max-content_1fr] tw-gap-x-4 tw-gap-y-1">
            <dt>
              <Text>{{ t('networking.port_forwarding_wan_ip') }}</Text>
            </dt>
            <dd>
              <Text class="tw-font-mono">{{ portForwarding.wan_ip || t('networking.port_forwarding_no_wan') }}</Text>
            </dd>

            <template v-if="portForwardingEnabled && portForwarding.wan_ip">
              <template v-for="link in portForwardingLinks" :key="link.port">
                <dt>
                  <Text>{{ link.label }}:</Text>
                </dt>
                <dd>
                  <Text>
                    <a :href="link.url" target="_blank" rel="noopener" class="tw-font-mono tw-underline">{{
                      link.url
                    }}</a>
                  </Text>
                </dd>
              </template>
            </template>
          </dl>
        </Box>
      </div>

      <div class="tw-rounded-[16px] tw-bg-white/5 tw-p-2">
        <iframe v-if="grafanaUrl" :src="grafanaUrl" class="tw-h-[820px] tw-w-full tw-rounded-[12px]" title="Grafana" />
        <Text v-else class="tw-text-center tw-text-white/89">{{ t('monitoring.grafana_unavailable') }}</Text>
      </div>
    </div>
  </TabPanel>
  <PortForwardingHelpModal v-model="portForwardingHelpOpen" />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Button, Checkbox, FormField, Heading, Icon, Spinner, TabPanel, Text } from '@slideslive/fuse-kit/vue';

import api from '@/api';
import InformationOutlineSvg from '@/assets/mdi/information-outline.svg';
import RouterWanSection from '@/components/RouterWanSection.vue';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';
import toBrowserHost from '@/utils/station_url';

import PortForwardingHelpModal from '../PortForwardingHelpModal.vue';

const { t } = useI18n();

const stateStore = useStateStore();
const { updateRemoteState } = stateStore;
const { state } = storeToRefs(stateStore);

const wan = computed(() => state.value?.data?.wan || {});
const app = computed(() => state.value?.data?.app || {});

// GL router: interfaces with priorities instead of the form.
const routerWanSupported = computed(() => wan.value.failover_supported === true);

const form = ref({
  command: 'wan_wifi',
  wifiSsid: '',
  wifiPassword: '',
  band: '2.4GHz',
  revertWhenFails: true,
  timeoutSec: '60',
});

const _prefilled = ref(false);

watch(
  wan,
  (next) => {
    if (!next || _prefilled.value) return;

    const type = next.type === 'ethernet' ? 'wan_ethernet' : 'wan_wifi';
    form.value.command = type;

    if (type === 'wan_wifi') {
      form.value.wifiSsid = (next.provider || '').toString();
      form.value.band = next.band || form.value.band;
    }

    _prefilled.value = true;
  },
  { immediate: true },
);

const submitting = ref(false);
const remainingSec = ref(null);

let _intervalId = null;
let _timeoutId = null;

onBeforeUnmount(() => {
  if (_intervalId) window.clearInterval(_intervalId);
  if (_timeoutId) window.clearTimeout(_timeoutId);
});

// Characters the MikroTik router may not read correctly (they break the RouterOS
// command quoting, or are non-ASCII). Warn only — submitting is still allowed.
const UNSAFE_WIFI_CHARS_RE = /[^ -~]|[`"'\\$?]/;

const ssidHasUnsafeChars = computed(() => UNSAFE_WIFI_CHARS_RE.test(form.value.wifiSsid || ''));
const passwordHasUnsafeChars = computed(() => UNSAFE_WIFI_CHARS_RE.test(form.value.wifiPassword || ''));

const canSubmit = computed(() => {
  if (form.value.command === 'wan_ethernet') return true;

  const ssidOk = (form.value.wifiSsid || '').trim().length > 0;

  if (!ssidOk) return false;

  if (form.value.revertWhenFails) {
    const tsec = Number(form.value.timeoutSec);
    if (!Number.isFinite(tsec) || tsec <= 0) return false;
  }

  return true;
});

const submit = async (event) => {
  if (event) event.preventDefault();
  if (!canSubmit.value || submitting.value) return;

  const payload = {};

  if (form.value.command === 'wan_ethernet') {
    payload.type = 'ethernet';
  } else {
    payload.type = 'wifi';
    payload.revert_when_fails = Boolean(form.value.revertWhenFails);
    payload.ping_timeout = payload.revert_when_fails ? Number(form.value.timeoutSec || 60) : 60;
    payload.ssid = (form.value.wifiSsid || '').trim();
    payload.password = (form.value.wifiPassword || '').trim();
    payload.band = form.value.band;
  }

  submitting.value = true;

  if (_intervalId) window.clearInterval(_intervalId);
  if (_timeoutId) window.clearTimeout(_timeoutId);

  if (payload.type === 'wifi' && payload.revert_when_fails) {
    remainingSec.value = Number(payload.ping_timeout) || 60;

    _intervalId = window.setInterval(() => {
      if (remainingSec.value === null) return;
      remainingSec.value = Math.max(0, Number(remainingSec.value) - 1);
    }, 1000);

    _timeoutId = window.setTimeout(
      () => {
        remainingSec.value = null;
        if (_intervalId) window.clearInterval(_intervalId);
        _intervalId = null;
      },
      (Number(payload.ping_timeout) || 60) * 1000,
    );
  } else {
    remainingSec.value = null;
  }

  try {
    // The response is an action result, not /state: the fetcher shows its notifications.
    await fetcher.post('/wan/-/configure', payload);
  } finally {
    submitting.value = false;
    if (!payload.revert_when_fails) remainingSec.value = null;

    // The operator may be connected through the WAN being switched, so the
    // configure response can get lost: pull fresh state independently.
    refreshState();
  }
};

const refreshState = async () => {
  try {
    const res = await api.state();
    if (res) updateRemoteState(res);
  } catch {
    // Regular /state polling keeps retrying; nothing to do here.
  }
};

// Older nate-runner versions have no port forwarding and do not send this field.
const portForwardingSupported = computed(() => state.value?.data?.port_forwarding != null);
const portForwarding = computed(() => state.value?.data?.port_forwarding || {});
const portForwardingEnabled = computed(() => portForwarding.value.status === 'enabled');
const portForwardingUnknown = computed(() => !portForwarding.value.status || portForwarding.value.status === 'unknown');
const portForwardingSubmitting = ref(false);
const portForwardingHelpOpen = ref(false);

// Forwarded ports worth a link, see PORT_FORWARDING_PORTS in nate-runner.
const PORT_FORWARDING_TARGETS = [
  { port: 80, label: 'networking.port_forwarding_runner' },
  { port: 5000, label: 'networking.port_forwarding_nate' },
];

const portForwardingLinks = computed(() =>
  PORT_FORWARDING_TARGETS.filter(({ port }) => (portForwarding.value.ports || []).includes(port)).map(
    ({ port, label }) => ({
      port,
      label: t(label),
      url: port === 80 ? `http://${portForwarding.value.wan_ip}/` : `http://${portForwarding.value.wan_ip}:${port}/`,
    }),
  ),
);

const setPortForwarding = async (enabled) => {
  if (portForwardingSubmitting.value) return;

  portForwardingSubmitting.value = true;

  try {
    await fetcher.post('/wan/-/port-forwarding', { enabled: Boolean(enabled) });
  } finally {
    portForwardingSubmitting.value = false;
  }
};

const grafanaUrl = computed(() => {
  return toBrowserHost(app.value?.dashboard_nate_networking_url?.trim(), app.value?.host_url);
});
</script>
